import { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SignInForm } from '@/components/auth/SignInForm';
import { BetaSignUpForm } from '@/components/auth/BetaSignUpForm';
import { ForgotPasswordForm } from '@/components/auth/ForgotPasswordForm';
import { InviteVerificationForm } from '@/components/users/InviteVerificationForm';
import { InvitePasswordCreationForm } from '@/components/users/InvitePasswordCreationForm';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

const authBgImage = '/bottle-mesh.jpg';

export default function Auth() {
  const { isAuthenticated, isLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('signin');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite");
  const forgotParam = searchParams.get("forgot");
  const [inviteStep, setInviteStep] = useState<"verify" | "password">("verify");
  const [verifiedInvitation, setVerifiedInvitation] = useState<any>(null);
  const [resetError, setResetError] = useState<string | null>(null);

  // Check for hash parameters indicating a password reset error
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const params = new URLSearchParams(hash.substring(1));
      const error = params.get('error');
      const errorCode = params.get('error_code');
      
      if (error && errorCode === 'otp_expired') {
        setShowForgotPassword(true);
        setResetError('Your password reset link has expired or was already used. Please request a new one.');
        // Clean up the hash
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    }
  }, []);

  // Check for forgot query parameter
  useEffect(() => {
    if (forgotParam === 'true') {
      setShowForgotPassword(true);
    }
  }, [forgotParam]);

  useEffect(() => {
    const img = new Image();
    img.src = authBgImage;
    img.onload = () => {
      setImageLoaded(true);
    };
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left side - Branding */}
      <div className="hidden lg:flex flex-col justify-center items-center relative p-12 text-primary-foreground overflow-hidden">
        {/* Background image layer */}
        <div
          className={cn(
            "absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000 ease-in-out",
            imageLoaded ? "opacity-100" : "opacity-0",
            !imageLoaded && "bg-primary/20"
          )}
          style={{ backgroundImage: `url(${authBgImage})` }}
        />
        {/* Stripe pattern layer */}
        <div
          className={cn(
            "absolute inset-0 transition-opacity duration-1000 ease-in-out"
          )}
          style={{ 
            backgroundImage: 'var(--bg-stripe-pattern)',
            opacity: imageLoaded ? 0.15 : 0
          }}
        />
        {/* Red overlay at 35% opacity with multiply blend mode */}
        <div 
          className={cn(
            "absolute inset-0 transition-all duration-1000 ease-in-out",
            imageLoaded ? "bg-[#CAC0B6]" : "bg-[#CAC0B6]"
          )}
          style={{ 
            opacity: imageLoaded ? 0.35 : 0.7,
            mixBlendMode: 'color'
          }} 
        />
        {/* Content on top */}
        <div className="relative z-10 max-w-md space-y-6">
          <div className="flex items-center gap-3">
            <img src="/gravity-assist-white-logo.svg" alt="Gravity Assist" className="h-12" />
          </div>
          <p className="text-lg opacity-90">
            Streamline your winery operations with intelligent workflow management,
            intake processing, and comprehensive cellar operations tracking.
          </p>
          <div className="space-y-4 pt-6">
            <div className="flex items-start gap-3">
              <div className="h-2 w-2 rounded-full bg-primary-foreground/80 mt-2" />
              <div>
                <h3 className="font-semibold">Automated Intake</h3>
                <p className="text-sm opacity-80">Parse emails and documents automatically</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="h-2 w-2 rounded-full bg-primary-foreground/80 mt-2" />
              <div>
                <h3 className="font-semibold">Work Order Management</h3>
                <p className="text-sm opacity-80">Track operations from planning to completion</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="h-2 w-2 rounded-full bg-primary-foreground/80 mt-2" />
              <div>
                <h3 className="font-semibold">Lab Testing</h3>
                <p className="text-sm opacity-80">Comprehensive quality control workflows</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Auth form */}
      <div className="flex items-center justify-center p-8 bg-background" style={{ backgroundImage: 'var(--bg-stripe-pattern)' }}>
        <div className="w-full max-w-md space-y-6">
          <div className="lg:hidden flex items-center gap-2 justify-center mb-8">
            <img src="/gravity-assist-logo.svg" alt="Gravity Assist" className="h-8" />
          </div>

          {inviteToken ? (
            inviteStep === "verify" ? (
              <InviteVerificationForm
                token={inviteToken}
                onVerified={(invitation) => {
                  setVerifiedInvitation(invitation);
                  setInviteStep("password");
                }}
              />
            ) : (
              <InvitePasswordCreationForm invitation={verifiedInvitation} />
            )
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>{showForgotPassword ? 'Reset Password' : 'Welcome'}</CardTitle>
                <CardDescription>
                  {showForgotPassword 
                    ? 'Enter your email to receive a password reset link'
                    : 'Sign in to your account or create a new one to get started'
                  }
                </CardDescription>
              </CardHeader>
              <CardContent>
                {showForgotPassword ? (
                  <ForgotPasswordForm 
                    onBackToSignIn={() => {
                      setShowForgotPassword(false);
                      setResetError(null);
                    }}
                    errorContext={resetError}
                  />
                ) : (
                  <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="signin">Sign In</TabsTrigger>
                      <TabsTrigger value="beta">Access</TabsTrigger>
                    </TabsList>
                    <TabsContent value="signin" className="mt-6">
                      <SignInForm onForgotPassword={() => setShowForgotPassword(true)} />
                    </TabsContent>
                    <TabsContent value="beta" className="mt-6">
                      <BetaSignUpForm />
                    </TabsContent>
                  </Tabs>
                )}
              </CardContent>
            </Card>
          )}

          <p className="text-center text-sm text-muted-foreground">
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
}
